/**
 * Stone Investment Project Showcase
 * 
 * Featured projects grid with Stone Investment's luxury aesthetic:
 * - Editorial layout
 * - Cinematic hover effects
 * - Region-aware styling
 */

'use client';

import { FormatPrice } from '@/components/ui/FormatPrice';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import { ArrowUpRight, ArrowRight, MapPin } from 'lucide-react';
import { detectRegion } from '../utils/regionColors';
import { HorizonOverlay } from './PropertyCard';

export interface ProjectShowcaseItem {
  id: string;
  name: string;
  slug: string;
  tagline?: string;
  location?: string;
  image?: string;
  status?: 'PLANNING' | 'UNDER_CONSTRUCTION' | 'COMPLETED' | 'SELLING';
  totalUnits?: number;
  priceFrom?: number;
  currency?: string;
}

export interface StoneProjectShowcaseProps {
  /**
   * Section title
   */
  title?: string;
  /**
   * Section subtitle/eyebrow
   */
  eyebrow?: string;
  /**
   * Projects to display
   */
  projects: ProjectShowcaseItem[];
  /**
   * Maximum items to show
   */
  maxItems?: number;
  /**
   * Base path for project links
   */
  basePath?: string;
  /**
   * View all link
   */
  viewAllLink?: string;
  /**
   * Current locale
   */
  locale?: string;
  /**
   * Translation messages
   */
  messages?: Record<string, string>;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * Individual project card component
 */
function ProjectCard({ project, basePath, locale, messages }: { 
  project: ProjectShowcaseItem; 
  basePath: string;
  locale: string;
  messages?: Record<string, string>;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const region = detectRegion(project.location || project.name);
  const isFrance = region === 'provence';
  const href = `/${locale}${basePath}/${project.slug}`;
  
  return (
    <Link
      href={href}
      className="group block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex flex-col gap-6">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-stone-200">
          {project.image ? (
            <Image
              src={project.image}
              alt={project.name}
              fill
              className={`object-cover transition-transform duration-[2000ms] ease-out ${
                isHovered ? 'scale-105' : 'scale-100'
              }`}
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-stone-300 to-stone-200" />
          )}
          
          {/* HorizonOverlay */}
          <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />
          
          {/* View Button */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none">
            <div 
              className={`relative flex items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] ${
                isHovered ? 'w-24 h-24 opacity-100' : 'w-12 h-12 opacity-0 translate-y-4'
              }`}
            >
              <div className="absolute inset-0 border-[0.5px] border-white/60 rounded-full animate-[spin_12s_linear_infinite]" />
              <span className="text-white font-serif italic text-xl tracking-widest drop-shadow-md">
                {messages?.view || 'View'}
              </span>
            </div>
          </div>
          
          {/* Location Badge */}
          <div className="absolute bottom-6 left-6 z-20 overflow-hidden">
            <p 
              className={`text-[10px] uppercase tracking-[0.3em] text-stone-200 font-sans font-medium transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                isHovered ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: '100ms' }}
            >
              {project.location || (messages?.viewProject || 'View Project')}
            </p>
          </div>
          
          {/* Status Badge */}
          {project.status && (
            <div className="absolute top-6 left-6 px-3 py-1.5 bg-stone-900 text-[10px] uppercase tracking-[0.2em] text-stone-100 font-sans">
              {(() => {
                const stageMap: Record<string, string> = {
                  UNDER_CONSTRUCTION: messages?.stageUnderConstruction || '',
                  PLANNING: messages?.stageInPlanning || '',
                  COMPLETED: messages?.stageCompleted || '',
                  SELLING: messages?.stageNowSelling || '',
                };
                return stageMap[project.status!] || project.status!.replace('_', ' ');
              })()}
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex flex-col gap-3 border-t border-stone-200 pt-6">
          {/* Location */}
          {project.location && (
            <div className="flex items-center gap-2 text-stone-500">
              <MapPin size={12} strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-[0.2em] font-sans">{project.location}</span>
            </div>
          )}
          
          {/* Title */}
          <h3 className={`font-serif text-2xl md:text-3xl text-stone-900 leading-tight transition-all duration-500 ${
            isHovered ? 'italic' : ''
          }`}>
            {project.name}
          </h3>
          
          {/* Tagline */}
          {project.tagline && (
            <p className="font-sans text-stone-600 text-sm line-clamp-2">
              {project.tagline}
            </p>
          )}
          
          {/* Stats */}
          <div className="flex items-center gap-6 text-sm font-sans text-stone-700 mt-2">
            {project.totalUnits && (
              <span>{project.totalUnits} {messages?.units || 'Units'}</span>
            )}
            {project.priceFrom && (
              <span className="font-medium">
                {messages?.from || 'From'} <FormatPrice amount={project.priceFrom} currency={project.currency || DEFAULT_CURRENCY} size="sm" className="inline" />
              </span>
            )}
          </div>
          
          {/* Arrow */}
          <div className={`transition-all duration-500 ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
            <ArrowUpRight size={20} className="text-stone-900" strokeWidth={1} />
          </div>
        </div>
      </div>
    </Link>
  );
}

/**
 * StoneProjectShowcase - Featured projects grid
 */
export function StoneProjectShowcase({
  title = 'Featured Projects',
  eyebrow = 'Developments',
  projects,
  maxItems = 3,
  basePath = '/projects',
  viewAllLink,
  locale = 'en',
  messages = {},
  className = '',
}: StoneProjectShowcaseProps) {
  const displayProjects = projects.slice(0, maxItems);
  
  if (projects.length === 0) {
    return null;
  }
  
  return (
    <section className={`py-20 md:py-32 bg-stone-50 px-6 md:px-12 ${className}`}>
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            {eyebrow && (
              <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-3">
                {eyebrow}
              </p>
            )}
            <h2 
              className="font-serif text-4xl md:text-5xl text-stone-900"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {title}
            </h2>
          </div>
          
          {viewAllLink && (
            <Link
              href={viewAllLink}
              className="inline-flex items-center gap-3 text-sm font-sans uppercase tracking-[0.15em] text-stone-700 hover:text-stone-900 transition-colors group"
            >
              <span>{messages.viewAll || 'View All Projects'}</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </Link>
          )}
        </div>
        
        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-16">
          {displayProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              basePath={basePath}
              locale={locale}
              messages={messages}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default StoneProjectShowcase;
